import { Injectable }   from "@angular/core";
import { environment }  from "src/environments/environment";
import { InfinityUtil, FormItem, bindAll, downloadPdf, httpPromise, timeMachine, toPhone } from "src/infinity-ui"; // { set, get, requiredReducer, itemReducer, store }

// Services
import { ApiService }   from "./api.service";
import { ModelService } from "./model.service"; // { participant }
import { ErrorService } from "./error.service";

// Templates & Static Data
import { arcRegisterFormSummary }   from "../templates/ARC.register-summary";
import { ataApplicationForm }       from "../templates/ATA.application-form";
import { umbrellaApplicationForm }  from "../templates/UMB.new-application-form";
import { umbrellaNeuberApplicationForm } from "../templates/UMB.neuber.application-form";

const RECAPTCHA_CONTAINER_CLASS = "recaptcha-container";
const SHOW_RECAPTCHA_CLASS = "show-recaptcha";
const APPLICATION_SUBMISSION_ERROR_MESSAGE = "The application could not be submitted.";

// Single source of truth for the Applied Pay back-button marker key, shared with AppComponent's
// pageshow handler so the two sides cannot drift.
export const APPLIED_PAY_REDIRECTED_KEY = "appliedPay.redirected";

@Injectable({ providedIn: "root" })
export class ActionService {

    constructor(public api: ApiService, public util: InfinityUtil, public model: ModelService, public errors: ErrorService) { bindAll(this); }

    /* ----- Landing ----- */

    initCaptcha() {
        const captchaContainer = document.getElementsByClassName(RECAPTCHA_CONTAINER_CLASS)[0];

        var captchaElement = document.createElement("div");
        var captchaConfig = { class: "g-recaptcha", "data-sitekey": environment.recaptchaSitekey, "data-callback": "verifyCaptcha" };
        Object.keys(captchaConfig).forEach(key => captchaElement.setAttribute(key, captchaConfig[key]));

        var captchaScript = document.createElement("script");
        var scriptConfig = { src: "https://www.google.com/recaptcha/api.js", async: true };
        Object.keys(scriptConfig).forEach(key => captchaScript.setAttribute(key, scriptConfig[key]));

        [captchaElement, captchaScript].forEach(e => captchaContainer.appendChild(e));
    }

    initApplicationForm(): FormItem | undefined {
        const { applicationName, programName } = this.util.store;
        if (applicationName === "ata") { return ataApplicationForm(this.util.store); }
        if (applicationName === "umbrella") {
            if (programName === "neuber")
            {
                return umbrellaNeuberApplicationForm(this.util.store);
            } else {
                return umbrellaApplicationForm(this.util.store);
            }
        }
        return undefined;
    }

    prefillParticipant(participant: any) {
        const changes = {};
        Object.keys(participant).forEach(key => {
            let item = this.util.store[key];
            if (!item) { return; }
            if (item.itemId === "state") {
                const state = this.util.store.states.filter(e => e.stateCode === participant[key])[0];
                item.value = (state || {}).stateName || "";
            }
            else if (item.itemId === "phone") {
                item.value = toPhone(participant[key]);
            }
            else {
                item.value = participant[key];
            }
            if (item.itemId === "email") {
                item.disabled = true;
            }
            changes[key] = item;
        });
        this.util.set(changes, { batch: true });
    }

    async submitVerifyEmailForm() {
        // Client side validation.
        if (!this.validateForm("verifyEmailForm")) { return; }

        // Display loading spinner.
        this.util.setItemValue("loading", true);

        // Get renewal email status.
        const verifyEmailResponse = await this.api.postVerifyEmail();
        if (!verifyEmailResponse) { return; }

        // Determine status from email and selection.
        const verificationStatus = this.util.get("verificationStatus");
        const selectedRenewal: boolean = this.util.get("clientType.value") === "isRenewal";
          const isNewBusiness = ["unknown", "newBusiness"].indexOf(verificationStatus) > -1;
        const isRenewal = verificationStatus === "renewal";
          const isLapseInCoverage = this.util.get("isLapseInCoverage");
          if (isNewBusiness) { await this.api.issueCapabilityForCurrentApplicant(); }
        if (this.showVerificationStatusError(verificationStatus)) { return; }

        this.updateVerificationUi(selectedRenewal, isNewBusiness, isRenewal, isLapseInCoverage);
    }

    private updateVerificationUi(selectedRenewal: boolean, isNewBusiness: boolean, isRenewal: boolean, isLapseInCoverage: boolean) {
        const selectRenewalReturnNewNoLapse = selectedRenewal && isNewBusiness && !isLapseInCoverage;
        const selectRenewalReturnNewLapse = selectedRenewal && isNewBusiness && isLapseInCoverage;
        const selectRenewalReturnRenewalNoLapse = selectedRenewal && isRenewal && !isLapseInCoverage;
        const selectRenewalReturnRenewalLapse = selectedRenewal && isRenewal && isLapseInCoverage;
        const selectNewReturnNewNoLapse = !selectedRenewal && isNewBusiness && !isLapseInCoverage;
        const selectNewReturnNewLapse = !selectedRenewal && isNewBusiness && isLapseInCoverage;
        const selectNewReturnRenewalNoLapse = !selectedRenewal && isRenewal && !isLapseInCoverage;
        const selectNewReturnRenewalLapse = !selectedRenewal && isRenewal && isLapseInCoverage;

        const updates: any = { scroll: 0 };
        if (selectRenewalReturnNewNoLapse) {
            updates.showAdditionalInformation = true;
            updates.section = "register";
        }
        if (selectRenewalReturnRenewalNoLapse || selectRenewalReturnRenewalLapse) {
            updates.showRenewalMessage = true;
        }
        if (selectNewReturnRenewalNoLapse || selectNewReturnRenewalLapse) {
            updates.showNewRenewalMessage = true;
        }
        if (selectRenewalReturnNewLapse || selectNewReturnNewNoLapse || selectNewReturnNewLapse) {
            updates.section = "register";
        }

        this.util.set(updates, { batch: true });
        this.util.setItemValue("loading", false);
    }

    private showVerificationStatusError(status: string): boolean {
        switch (status) {
            case "alreadyRenewed": this.errors.alreadyRenewed(); return true;
            case "renewalTooEarly": this.errors.renewalTooEarly(); return true;
            case "policyNotYetInEffect": this.errors.policyNotYetInEffect(); return true;
            default: return false;
        }
    }

    /* ----- Register ----- */

    async backToLanding() {
        this.util.set({ section: "landing", scroll: 0 }, { batch: true });
    }

    async submitRegisterForm() {
        // Client side validation.
        if (!this.validateForm("registerForm")) { this.errors.registerFormInvalid(); return; }

        let w: any = window;

        if (environment.useRecaptcha) {
            // Display Captcha
            document.getElementsByClassName("recaptcha-message")[0].setAttribute("style", "");
            const recaptcha = document.getElementsByClassName(RECAPTCHA_CONTAINER_CLASS)[0];
            recaptcha.classList.add(SHOW_RECAPTCHA_CLASS);

            // Get captcha response.
            w.verifyCaptcha = async (token) => {
                // Hide captcha.
                recaptcha.classList.remove(SHOW_RECAPTCHA_CLASS);

                // Verify captcha token.
                var res = await httpPromise("POST", environment.apiBase + "/api/intake/verifyCaptcha", { data: { token } });
                w.captchaVerified = (res || {}).success || false;
            };
        }
        else {
            w.captchaVerified = true;
        }

        // Wait for captcha response before submitting (we have to keep the "thread" in angular, it won't work if we do this in the promise above).
        var waitForCaptcha = setInterval(async () => {
            if (w.captchaVerified === undefined) { return; }
            clearInterval(waitForCaptcha);
            if (w.captchaVerified === true) {
                // Display loading spinner.
                this.util.setItemValue("loading", true);
                try {
                    // Store coverage information.
                    const selectedCoverages = this.getSelectedCoverages(this.util.store.coverageInformation);
                    this.util.set({ participant: this.util.get(this.model.participant), selectedCoverages }, { batch: true });

                    // Validate/rate on the backend. V2 preview must complete
                    // before the payment page is made visible.
                    const formValidationResponse = await this.api.postFormValidation();
                    if (!formValidationResponse) { return; }

                    this.syncAppliedPayPaymentControls();

                    // Summary hydration is presentation-only; it must not
                    // prevent a valid preview from reaching payment.
                    try {
                        this.addSummaryToPaymentPage();
                    } catch (summaryError) {
                        console.warn("Payment summary was not ready after rate preview", summaryError);
                    }
                    this.clearAppliedPayResume();
                    this.util.set({ appliedPaySavedHash: undefined, section: "payment", scroll: 0 }, { batch: true });
                } catch (error) {
                    // The old interval callback allowed rejected promises to
                    // leave the global spinner running forever. Surface the
                    // failure and keep the user on the coverage page.
                    console.error("Unable to prepare payment page after rate preview", error);
                    const message = this.getRatePreviewErrorMessage(error);
                    this.util.setItemValue("error", `<h2>Incorrect premium selection</h2><p>${this.escapeHtml(message)}</p>`);
                } finally {
                    this.util.setItemValue("loading", false);
                }
            }
            else {
                // Display error.
                this.errors.captchaError();
            }
        }, 200);
    }

    private getRatePreviewErrorMessage(error: any): string {
        const response = error?.response;
        const firstError = Array.isArray(response?.errors) ? response.errors[0] : null;
        const details = response?.detail || response?.message || response?.errorDescription ||
            firstError?.errorDescription || firstError?.message ||
            (typeof response?.errors === "string" ? response.errors : null) ||
            response?.title || error?.message;
        if (typeof details === "string" && details.trim()) { return details; }
        return "The premium selection you entered is invalid. Please review your coverage and try again.";
    }

    private escapeHtml(value: string): string {
        return value.replace(/[&<>"']/g, character => ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            "\"": "&quot;",
            "'": "&#39;"
        })[character]);
    }

    async submitAdditionalInfo() {
        // Client side validation.
        if (!this.validateForm("registerForm")) { this.errors.registerFormInvalid(); return; }

        // Display loading spinner.
        this.util.setItemValue("loading", true);

        // Submit to backend.
        const additionalInfoResponse = await this.api.postAdditionalInfo();
        if (!additionalInfoResponse) { return; }

        // Hide loading spinner.
        this.util.set({ section: "receipt", scroll: 0 }, { batch: true });

        this.util.setItemValue("loading", false);
    }

    addSummaryToPaymentPage() {
        const { paymentForm, registerFormSummary } = this.util.store;                       // Get payment form and summary from store.
        if (!paymentForm || !Array.isArray(paymentForm.items)) { return false; }
        this.syncAppliedPayPaymentControls();
        if (registerFormSummary) { paymentForm.items.shift(); }                                 // Remove old summary if one exists.
        this.util.set({ registerFormSummary: arcRegisterFormSummary(this.util.store) });    // Store new summary.
        paymentForm.items.unshift(this.util.store.registerFormSummary);                     // Add new summary to paymentForm.
        return true;
    }

    syncAppliedPayPaymentControls(): boolean {
        const paymentProvider = this.api.getPaymentProviderForUi?.() ||
            (this.api.isAppliedPayProviderActiveForUi() ? "AppliedPay" : "CardConnect");
        const appliedPayActive = paymentProvider === "AppliedPay";
        const updates: any = { appliedPayActive };
        const setVisible = (itemId: string, visible: boolean) => {
            const item = this.util.get(itemId);
            if (!item) { return; }
            item.visible = visible;
            updates[itemId] = item;
        };

        setVisible("paymentType", !appliedPayActive);
        setVisible("genericPayment", !appliedPayActive);
        setVisible("creditCardPayment", false);
        setVisible("achPayment", false);
        setVisible("paymentButtons", !appliedPayActive);
        setVisible("appliedPayButtons", appliedPayActive);
        const providerVerbiage = this.util.get("appliedPayVerbiage");
        if (providerVerbiage) {
            providerVerbiage.label = paymentProvider === "AppliedPay"
                ? "<p>This link will transfer you to a dedicated payment page hosted by Applied Systems, Inc.</p>"
                : "<p>Enter your credit card or bank account information below to pay securely through Card Connect.</p>";
            updates.appliedPayVerbiage = providerVerbiage;
        }
        setVisible("appliedPayVerbiage", paymentProvider !== "None");

        const paymentType = this.util.get("paymentType");
        if (paymentType) {
            paymentType.required = !appliedPayActive;
            if (appliedPayActive) {
                paymentType.value = undefined;
                paymentType.valid = true;
            }
            updates.paymentType = paymentType;
        }

        this.util.set(updates, { batch: true });
        return appliedPayActive;
    }

    /* ----- Payment ----- */

    async cancelPremiumFinance() {
        const { paymentType, premiumFinancePaymentType } = this.util.store;
        paymentType.value = undefined;
        premiumFinancePaymentType.value = undefined;
        this.util.set({ paymentType, premiumFinancePaymentType, premiumFinanceResponse: undefined }, { batch: true });
    }

    async submitPremiumFinanceRequest() {
        // Client side validation.
        if (!this.validateForm("premiumFinanceRequest")) { return; }

        // Display loading spinner.
        this.util.setItemValue("loading", true);

        // Get premium finance quote.
        const premiumFinanceResponse = await this.api.postPremiumFinance();
        if (!premiumFinanceResponse) { return; }

        // Move to premium finance terms section.
        this.util.set({ premiumFinanceSection: "terms" }, { batch: true });

        // Hide loading spinner.
        this.util.setItemValue("loading", false);
    }

    async downloadPremiumFinanceAgreement() {
        const { viewPremiumFinanceAgreement } = this.util.store;
        viewPremiumFinanceAgreement.valid = true;
        downloadPdf(this.util.get("pfPdfAgreement"));
        this.util.set({ viewPremiumFinanceAgreement }, { batch: true });
    }

    async submitPremiumFinanceTerms() {
        if (!this.validateForm("premiumFinanceTerms")) { return; }
        this.util.set({ premiumFinanceSection: "payment" }, { batch: true });
    }

    async backToRegister() {
        this.util.set({ section: "register", scroll: 0 }, { batch: true });
    }

    private capabilityAllows(...operations: string[]): boolean {
        if (!sessionStorage.getItem("arc_capability_access_token")) { return true; }
        const operation = this.util.get("capabilityContext.operation");
        // New-business typed-email capabilities are scope-authorized and do
        // not have a returning-link operation claim. The API validates their
        // payment scope/binding; this UI guard is only for returning links.
        if (!operation) { return true; }
        return operations.indexOf(operation) >= 0;
    }

    async submitPaymentForm() {
        this.syncAppliedPayPaymentControls();

        if (!this.canSubmitPayment()) {
            return;
        }

        if (this.util.store.appliedPayActive) {
            await this.submitAppliedPayForm();
            return;
        }

        this.util.setItemValue("loading", true);
        await this.waitForCardTokenization();
        if (!await this.validatePaymentOrSaveQuote()) {
            return;
        }

        if (!await this.submitParticipantEnrollment()) {
            return;
        }

        if (this.api.isV2WebIntakeForUi()) {
            await this.completeV2Intake();
            return;
        }

        await this.submitLegacyPayment();
    }

    private canSubmitPayment(): boolean {
        if (this.capabilityAllows("payment:initiate", "quote:resume", "renewal:resume")) {
            return true;
        }

        this.util.setItemValue("error", "This link is not authorized for payment or quote completion.");
        return false;
    }

    private async waitForCardTokenization(): Promise<void> {
        if (this.util.get("paymentType.value") === "creditcard") {
            await new Promise(resolve => setTimeout(resolve, 2000));
        }
    }

    private async validatePaymentOrSaveQuote(): Promise<boolean> {
        if (!this.validateForm("paymentForm")) {
            this.util.setItemValue("loading", false);
            return false;
        }

        if (this.util.get("paymentType.value") !== "saveQuote") {
            return true;
        }

        const response = await this.api.putSaveQuote();
        if (response && (response.isSuccessful || response.outcome || response.participantRatingId)) {
            this.util.setItemValue("loading", false);
        }
        return false;
    }

    private async submitParticipantEnrollment(): Promise<boolean> {
        if (this.util.store.participantToken) {
            return true;
        }

        try {
            const response = await this.api.putParticipantEnrollment();
            if (response) {
                return true;
            }
        } catch (error: any) {
            console.error("V2 intake failed", error);
            this.util.setItemValue("error", error?.detail || error?.message || APPLICATION_SUBMISSION_ERROR_MESSAGE);
        }

        this.util.setItemValue("loading", false);
        return false;
    }

    private async completeV2Intake(): Promise<void> {
        const response: any = this.util.get("participantEnrollmentResponse");
        const failed = !!response && (
            Number(response.status) >= 400 ||
            typeof response.type === "string" && response.type.indexOf("/problems/") >= 0);
        if (failed) {
            this.util.setItemValue("error", response.detail || response.title || APPLICATION_SUBMISSION_ERROR_MESSAGE);
            this.util.setItemValue("loading", false);
            return;
        }

        const outcome = this.util.get("participantEnrollmentResponse.outcome") || {};
        this.util.set({
            receiptEmail: this.model.setItemValue("receiptEmail", this.util.get("participant.email")),
            transactionNumber: this.model.setItemValue("transactionNumber", outcome.transactionNumber),
            confirmationNumber: this.model.setItemValue("confirmationNumber", outcome.confirmationNumber),
            paymentMessage: this.model.setItemValue("paymentMessage", outcome.message)
        }, { batch: true });
        this.addSummaryToReceiptPage();
        this.util.set({ section: "receipt", scroll: 0 }, { batch: true });
        this.util.setItemValue("loading", false);
    }

    private async submitLegacyPayment(): Promise<void> {
        const paymentResponse = await this.api.postPayment();
        if (!paymentResponse || !paymentResponse.isSuccessful || !paymentResponse.transactionNumber) {
            this.util.setItemValue("error", "");                                            // Hide error.
            await this.api.putPaymentError();                                               // Update participant with payment error status.
            this.util.set({ section: "receipt", paymentErrorHack: true }, { batch: true }); // Go to receipt and display message.
            this.util.setItemValue("loading", false);                                       // Hide loading screen.
            return;
        }

        // Posting status.
        const postingStatusResponse = await this.api.postPostingStatus();
        if (!postingStatusResponse) { return; }

        // Receipt email.
        // Payment is already confirmed and its receipt has been persisted server-side. Email
        // delivery is best-effort; a notification/auth failure must not strand the paid user
        // before the receipt page or encourage them to retry payment.
        try {
            await this.api.postPaymentReceiptEmail();
        } catch (error) {
            console.error("Payment succeeded, but the receipt email request failed.", error);
        }

        // Ping post pay correspondence.
        try {
            await this.api.pingPostPayCorrespondence();
        } catch (error) {
            console.error("Payment succeeded, but post-pay correspondence could not be requested.", error);
        }

        // Add summary to receipt page.
        this.addSummaryToReceiptPage();

        // Continue to receipt.
        this.util.set({ section: "receipt", scroll: 0 }, { batch: true });

        // Hide loading spinner.
        this.util.setItemValue("loading", false);

        // Ping AMS.
        await this.api.pingAms();
    }

    appliedPayResumeKey = "appliedPay.resume";
    appliedPayReturnKey = "appliedPay.return";
    appliedPaySubmitting = false;

    async submitAppliedPayForm() {
        if (this.api.isV2WebIntakeForUi() &&
            !this.util.get("capabilityContext.operation") &&
            this.api.isCapabilityExpiredOrExpiryUnknown?.()) {
            this.showExpiredCapabilityPrompt();
            return;
        }

        const resume = this.getAppliedPayResume();
        if (resume) {
            this.setAppliedPayLoading(true);
            this.redirectToPaymentPage(resume.linkUrl);
            return;
        }

        if (!this.validateForm("paymentForm")) {
            return;
        }

        if (this.appliedPaySubmitting) {
            return;
        }
        this.appliedPaySubmitting = true;

        try {
            const captchaToken = await this.getAppliedPayCaptchaToken();
            if (environment.useRecaptcha && !captchaToken) {
                this.errors.captchaError();
                return;
            }
            this.util.set({ captchaToken }, { batch: true });

            this.setAppliedPayLoading(true);

            if (this.api.isV2WebIntakeForUi()) {
                await this.redirectToAppliedPayV2Checkout();
                return;
            }

            await this.redirectToAppliedPayLegacyCheckout();
        } finally {
            this.appliedPaySubmitting = false;
        }
    }

    private showExpiredCapabilityPrompt(): void {
        const modal = this.util.get("capabilityExpiryModal");
        if (modal) {
            modal.value = `
                <div class="capability-expiry-card" role="alertdialog" aria-labelledby="capability-expired-title" aria-describedby="capability-expired-message">
                    <h2 id="capability-expired-title">Your session has expired</h2>
                    <p id="capability-expired-message">Refresh your session to continue. Your application details will remain on this page.</p>
                    <button type="button" class="capability-expiry-continue">Refresh session</button>
                </div>`;
            modal.visible = true;
            this.util.set({ capabilityExpiryModal: modal }, { batch: true });
            window.dispatchEvent(new Event("arc-capability-expiry-prompt"));
            return;
        }
        this.util.setItemValue("error", "Your session has expired. Refresh the page and start over to continue.");
    }

    async retryAppliedPayAfterTimeout(): Promise<void> {
        this.setAppliedPayLoading(true);
        const renewed = await this.api.renewCapabilityForCurrentApplicant();
        if (!renewed) {
            this.setAppliedPayLoading(false);
            this.util.setItemValue("error", "We could not refresh your session. Please try again.");
            return;
        }

        // Applied Pay timed out after creating an attempt. Start a fresh V2
        // intake with the current form, never reusing that attempt's rating or link.
        this.discardAppliedPayAttempt();
        this.util.setItemValue("error", "");
        this.setAppliedPayLoading(false);
        await this.submitAppliedPayForm();
    }

    private discardAppliedPayAttempt(): void {
        this.clearAppliedPayResume();
        this.clearAppliedPayReturnSnapshot();
        sessionStorage.removeItem("arc_intake_update_capability_token");
        this.util.set({
            participantRatingId: undefined,
            paymentSessionToken: undefined,
            participantEnrollmentResponse: undefined,
            v2SubmissionResponse: undefined,
            v2ParticipantRatingResponse: undefined,
            participantRatingSummaryResponse: undefined,
            capabilityContext: undefined,
            capabilityWorkflow: undefined,
            appliedPaySavedHash: undefined
        }, { batch: true });
    }

    private async redirectToAppliedPayV2Checkout(): Promise<void> {
        const intakeResponse = await this.submitAppliedPayV2Intake();
        if (!intakeResponse) {
            return;
        }

        const hostedPageUrl = intakeResponse?.payment?.redirectUrl;
        if (!this.isValidPaymentUrl(hostedPageUrl)) {
            this.discardAppliedPayAttempt();
            this.setAppliedPayLoading(false);
            this.errors.appliedPayInitiateError();
            return;
        }

        this.saveAppliedPayReturnSnapshot();
        this.setAppliedPayResume(hostedPageUrl);
        this.redirectToPaymentPage(hostedPageUrl);
    }

    private async redirectToAppliedPayLegacyCheckout(): Promise<void> {
        const formHash = this.getAppliedPayFormHash();
        const alreadySaved = this.util.store.participantRatingId && this.util.store.appliedPaySavedHash === formHash;
        if (!alreadySaved && !await this.saveAppliedPayLegacyEnrollment(formHash)) {
            return;
        }

        const hostedPageUrl = await this.createAppliedPayLegacyHostedPageUrl();
        if (!this.isValidPaymentUrl(hostedPageUrl)) {
            this.discardAppliedPayAttempt();
            this.setAppliedPayLoading(false);
            this.errors.appliedPayInitiateError();
            return;
        }

        this.saveAppliedPayReturnSnapshot();
        this.setAppliedPayResume(hostedPageUrl);
        this.redirectToPaymentPage(hostedPageUrl);
    }

    private async saveAppliedPayLegacyEnrollment(formHash: string): Promise<boolean> {
        let participantEnrollmentResponse;
        try {
            participantEnrollmentResponse = await this.api.putParticipantEnrollment();
        } catch (e) {
            participantEnrollmentResponse = null;
        }
        if (!participantEnrollmentResponse) {
            this.setAppliedPayLoading(false);
            return false;
        }
        this.util.set({ appliedPaySavedHash: formHash }, { batch: true });
        return true;
    }

    private async createAppliedPayLegacyHostedPageUrl(): Promise<string> {
        try {
            const initiateResponse = await this.api.postAppliedPayInitiate();
            return initiateResponse?.hostedPageUrl || "";
        } catch (e) {
            return "";
        }
    }

    private async submitAppliedPayV2Intake(): Promise<any> {
        let participantEnrollmentResponse;
        try {
            participantEnrollmentResponse = await this.api.putParticipantEnrollment();
        } catch (error: any) {
            this.setAppliedPayLoading(false);
            const problem = error?.response || error?.error || error;
            if (Number(problem?.status) >= 400 && Number(problem?.status) < 600) {
                // Any failed Applied Pay intake abandons the rating created
                // for this attempt, independent of the problem type.
                this.discardAppliedPayAttempt();
                this.errors.appliedPayInitiateError();
                return null;
            }
            this.util.setItemValue("error", error?.detail || error?.message || APPLICATION_SUBMISSION_ERROR_MESSAGE);
            return null;
        }

        if (!participantEnrollmentResponse) {
            this.setAppliedPayLoading(false);
            this.util.setItemValue("error", APPLICATION_SUBMISSION_ERROR_MESSAGE);
            return null;
        }

        const failed = !!participantEnrollmentResponse && (
            Number(participantEnrollmentResponse.status) >= 400 ||
            typeof participantEnrollmentResponse.type === "string" && participantEnrollmentResponse.type.indexOf("/problems/") >= 0);
        if (failed) {
            this.setAppliedPayLoading(false);
            if (Number(participantEnrollmentResponse.status) >= 400 && Number(participantEnrollmentResponse.status) < 600) {
                this.discardAppliedPayAttempt();
                this.errors.appliedPayInitiateError();
                return null;
            }
            this.util.setItemValue("error", participantEnrollmentResponse.detail || participantEnrollmentResponse.title || APPLICATION_SUBMISSION_ERROR_MESSAGE);
            return null;
        }

        return participantEnrollmentResponse;
    }

    getAppliedPayCaptchaToken(): Promise<string> {
        if (!environment.useRecaptcha) {
            return Promise.resolve("");
        }
        const w: any = window;
        return new Promise(resolve => {
            const message = document.getElementsByClassName("recaptcha-message")[0];
            if (message) {
                message.setAttribute("style", "");
            }
            const recaptcha = document.getElementsByClassName(RECAPTCHA_CONTAINER_CLASS)[0];
            if (w.grecaptcha) {
                w.grecaptcha.reset();
            }
            recaptcha.classList.add(SHOW_RECAPTCHA_CLASS);
            w.verifyCaptcha = (token) => {
                recaptcha.classList.remove(SHOW_RECAPTCHA_CLASS);
                resolve(token || "");
            };
        });
    }

    setAppliedPayLoading(isLoading: boolean) {
        this.util.setItemValue("loading", isLoading);
        const redirectModal = this.util.get("appliedPayRedirectModal");
        if (redirectModal) {
            redirectModal.visible = isLoading;
            this.util.set({ appliedPayRedirectModal: redirectModal }, { batch: true });
        }
        // Toggle the disabled state on whichever Pay button is present: the legacy button (Applied Pay OFF)
        // and the Applied Pay-specific button (Applied Pay ON).
        ["submitPayment", "appliedPaySubmitPayment"].forEach(id => {
            const button = this.util.get(id);
            if (button) {
                button.disabled = isLoading;
                this.util.set({ [id]: button }, { batch: true });
            }
        });
    }

    isValidPaymentUrl(url: string): boolean {
        return !!url && /^https:\/\//i.test(url);
    }

    readonly appliedPayRedirectedKey = APPLIED_PAY_REDIRECTED_KEY;

    redirectToPaymentPage(url: string) {
        if (!this.isValidPaymentUrl(url)) {
            this.errors.appliedPayInitiateError();
            return;
        }
        const expiryPrompt = this.util.get("capabilityExpiryModal");
        if (expiryPrompt?.visible) {
            this.setAppliedPayLoading(false);
            return;
        }
        // Mark that we are leaving for the Applied Pay Hosted Payment Page. On browser Back, the
        // pageshow handler in AppComponent uses this marker to force a fresh, blank submission form
        // (see app.component.ts). Scoped to the Applied Pay redirect only, so Applied Pay OFF is unaffected.
        // Best-effort: sessionStorage can throw (private mode / quota / disabled). A failure here must
        // not block the redirect, so guard it like the resume-storage helpers do.
        try {
            sessionStorage.setItem(this.appliedPayRedirectedKey, "1");
        } catch {
            // ignore — the back-button freshness marker is best-effort.
        }
        window.onbeforeunload = null;
        this.navigateToPaymentPage(url);
    }

    // Seam so tests can assert the redirect without the Karma runner actually navigating.
    navigateToPaymentPage(url: string) {
        window.location.href = url;
    }

    getAppliedPayFormHash(): string {
        return JSON.stringify(this.util.get(this.model.participantEnrollmentRequest));
    }

    setAppliedPayResume(hostedPageUrl: string) {
        const payload = {
            participantRatingId: this.util.get("participantRatingId"),
            paymentSessionToken: this.util.get("paymentSessionToken"),
            linkUrl: hostedPageUrl,
            amount: this.util.get("total"),
            formHash: this.getAppliedPayFormHash()
        };
        try { sessionStorage.setItem(this.appliedPayResumeKey, JSON.stringify(payload)); } catch (e) {
            // Storage is best-effort; payment navigation remains functional when unavailable.
        }
    }

    getAppliedPayResume(): any {
        let payload: any;
        try {
            payload = JSON.parse(sessionStorage.getItem(this.appliedPayResumeKey));
        } catch (e) {
            return null;
        }
        if (!payload?.linkUrl) {
            return null;
        }
        if (payload.formHash !== this.getAppliedPayFormHash()) {
            return null;
        }
        return payload;
    }

    clearAppliedPayResume() {
        try { sessionStorage.removeItem(this.appliedPayResumeKey); } catch (e) {
            // Storage is best-effort; there is no user-visible failure to surface here.
        }
    }

    // Snapshot the Contact / Coverage / Premium summary + email before redirecting to the hosted payment page,
    // so the Applied Pay return page can render them without a backend call (the redirect only carries the
    // payment result). Same-tab sessionStorage, mirroring the resume payload.
    saveAppliedPayReturnSnapshot() {
        const s: any = this.util.store;
        const p: any = s.participant || {};
        const snapshot = {
            participant: {
                firstName: p.firstName, middleName: p.middleName, lastName: p.lastName,
                businessName: p.businessName, email: p.email, phone: p.phone,
                addressLine1: p.addressLine1, addressLine2: p.addressLine2,
                city: p.city, zipCode: p.zipCode, state: p.state
            },
            state: { value: s.state ? s.state.value : p.state },
            prefix: { value: s.prefix ? s.prefix.value : "" },
            suffix: { value: s.suffix ? s.suffix.value : "" },
            pfDownPayment: { label: s.pfDownPayment ? s.pfDownPayment.label : "" },
            programName: s.programName,
            baseUrl: s.baseUrl,
            programTitle: s.programTitle,
            effectiveDates: s.effectiveDates,
            carrier: s.carrier,
            isReferral: s.isReferral,
            premium: s.premium, adjustedPremium: s.adjustedPremium, total: s.total,
            agencyFee: s.agencyFee, taxes: s.taxes, fees: s.fees,
            surcharge: s.surcharge, surchargeType: s.surchargeType,
            amountFinanced: s.amountFinanced,
            itemSummaries: s.itemSummaries || []
        };
        try { sessionStorage.setItem(this.appliedPayReturnKey, JSON.stringify(snapshot)); } catch (e) {
            // Best-effort browser return snapshot; continue when storage is unavailable.
        }
    }

    getAppliedPayReturnSnapshot(): any {
        try { return JSON.parse(sessionStorage.getItem(this.appliedPayReturnKey)); } catch (e) { return null; }
    }

    clearAppliedPayReturnSnapshot() {
        try { sessionStorage.removeItem(this.appliedPayReturnKey); } catch (e) {
            // Best-effort cleanup; continue when storage is unavailable.
        }
    }

    addSummaryToReceiptPage() {
        this.util.set({ registerFormSummary: arcRegisterFormSummary(this.util.store) });    // Create new summary.
        this.util.store.receiptSummary.items.push(this.util.store.registerFormSummary);     // Add summary to receipt.
    }

    /* ----- Middleware ----- */

    proxyLoad(fn: Function) {
        timeMachine([
            [0, () => this.util.setItemValue("loading", true)],
            [300, fn],
            [0, () => this.util.setItemValue("loading", false)]
        ]);
    }

    validateForm(itemId: string) {
        const valid = this.util.requiredReducer(true, this.util.get(itemId));
        if (!valid) { return (document.body.classList.add("display-required"), false); }
        return (document.body.classList.remove("display-required"), true);
    }

    getSelectedCoverages(formItem: any): any {
        const allItems = this.util.itemReducer([], formItem);
        const filterTypes = ["aggregate", "submit"];
        const filterIds = Object.keys(this.model.participant);
        const filteredItems = allItems.filter(item => item.value && filterTypes.indexOf(item.type) < 0 && filterIds.indexOf(item.itemId) < 0);
        return filteredItems.map(item => {
            let { type, value, items, itemId, label } = item;
            if (!item.data) { item.data = { semanticItemId: itemId, display: label }; }
            if (["number", "text", "date", "email", "textarea"].indexOf(type) > -1) {
                value = (value && value[0] === "$") ? value.replace(/[$,]/g, "") : value;
                item.data.value = type === "number" ? parseInt(value) : value;
                return item.data;
            }
            if ("radioGroup" === type || "table" === type) {
                const child = items.filter(child => child.itemId === value)[0];
                const selected = child.data
                    ? { ...child.data }
                    : { semanticItemId: child.itemId, display: child.label };

                selected.parentItemId = item.itemId || selected.parentItemId;
                selected.parentRatingItemDescription = item.label ||
                    item.data?.ratingItemDescription ||
                    item.data?.display ||
                    selected.parentRatingItemDescription ||
                    "";
                selected.ratingGroupDescription = selected.ratingGroupDescription ||
                    item.data?.ratingGroupDescription ||
                    "";

                return selected;
            }
            if ("dropdown" === type) {
                const child = items.filter(child => child.label === value)[0];
                return child.data || { semanticItemId: child.itemId, display: child.label };
            }
            if ("attachment" === type) {
                item.data.attachments = [{
                    ParticipantRatingItemId: itemId,
                    Title: value.name,
                    AttachmentType: "Document",
                    Attachment: value.encodedBinary
                }];
                return item.data;
            }
            if ("checkbox" === type) {
                item.data.value = true;
                return item.data;
            }
            return null;
        });
    }

}
